import instance, { ADMIN_PREFIX } from 'services/api';
import { AdminUser } from 'types/api/profile';
import {
  AuthQrResult,
  Disable2faInput,
  RecoveryCodesData,
  RecoveryCodesInput,
  SignInData,
  SignInInput,
  Verify2faData,
  Verify2faInput,
  VerifyOtpData,
  VerifyOtpInput
} from 'types/api/auth';

/**
 * POST /auth/sign-in — credential exchange. Returns the tmpToken (for
 * the optional 2FA flow) plus token/refreshToken when 2FA is off.
 * withCredentials=true so the BE can set its HttpOnly session cookie.
 */
export const signIn = async (input: SignInInput): Promise<SignInData> => {
  const res = await instance.post(`${ADMIN_PREFIX}/auth/sign-in`, input, {
    withCredentials: true
  });

  return res?.data?.data ?? res?.data;
};

/**
 * POST /auth/verify-otp — the SIGN-IN second step. The body is
 * `{otp, tmpToken}`; `otp` now also accepts a recovery code.
 *
 * No longer dual-purpose: enabling 2FA from settings goes through
 * `verify2fa` (POST /auth/2fa/verify). Note the paths differ from the
 * patient app — admin keeps /auth/verify-otp here.
 *
 * The tmpToken is single-use, but it is spent only by a SUCCESSFUL
 * verify: a wrong code leaves it alive, so retry in place on a 400 and
 * send the user back to sign-in only on a 401.
 */
export const verifyOtp = async (
  input: VerifyOtpInput
): Promise<VerifyOtpData> => {
  const res = await instance.post(`${ADMIN_PREFIX}/auth/verify-otp`, input);

  return res?.data?.data ?? res?.data ?? {};
};

/**
 * POST /auth/logout — JWT-scoped (Authorization header set by the
 * instance interceptor). Caller is responsible for clearing storage +
 * dispatching state cleanup.
 */
export const logout = (): Promise<unknown> =>
  instance.post(`${ADMIN_PREFIX}/auth/logout`);

/**
 * GET /auth/me — returns the calling admin's record with role,
 * roleName, and permissions pre-rolled (P20.2 closure).
 */
export const fetchMe = async (): Promise<AdminUser | null> => {
  const res = await instance.get(`${ADMIN_PREFIX}/auth/me`);

  return res?.data?.data ?? res?.data ?? null;
};

/**
 * POST /auth/qr — provisions a fresh 2FA QR + secret pair for the
 * calling admin. Used by the Authentication settings panel before
 * the OTP-verify confirmation step.
 */
export const generateAuthQr = async (): Promise<AuthQrResult> => {
  const res = await instance.post(`${ADMIN_PREFIX}/auth/qr`);

  return res?.data?.data ?? res?.data;
};

/**
 * POST /auth/2fa/verify — ACTIVATES 2FA for the calling admin. Bearer
 * only; the body is `{otp}`.
 *
 * This replaced the old pairing confirmation, which called
 * /auth/verify-otp with the access token and then wrote
 * `settings.auth2fa = true`. That settings write is ignored by the
 * backend now, so activation lives here and nowhere else — and this is
 * the only place the recovery codes are ever returned.
 */
export const verify2fa = async (
  input: Verify2faInput
): Promise<Verify2faData> => {
  const res = await instance.post(`${ADMIN_PREFIX}/auth/2fa/verify`, input);

  return res?.data?.data ?? res?.data ?? {};
};

/**
 * POST /auth/2fa/disable — turns 2FA off. Takes a 6-digit code OR a
 * recovery code. Replaced the settings write, which no longer does
 * anything.
 */
export const disable2fa = async (
  input: Disable2faInput
): Promise<{ message?: string }> => {
  const res = await instance.post(`${ADMIN_PREFIX}/auth/2fa/disable`, input);

  return res?.data?.data ?? res?.data ?? {};
};

/**
 * POST /auth/2fa/recovery-codes — issues a fresh set of ten and
 * invalidates every previous one. Requires a code FROM THE APP.
 */
export const regenerateRecoveryCodes = async (
  input: RecoveryCodesInput
): Promise<RecoveryCodesData> => {
  const res = await instance.post(
    `${ADMIN_PREFIX}/auth/2fa/recovery-codes`,
    input
  );

  return res?.data?.data ?? res?.data ?? {};
};

/**
 * POST <route> { email } — generic resend helper for confirmation /
 * password-reset emails. `route` is one of the unauth /auth/* paths
 * the BE exposes (forgot-password, reset-password, etc.).
 */
export const resendAuthEmail = (route: string, email: string) =>
  instance.post(route, { email });
